import { DcRenderingGroup } from "./DcRenderingGroup";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { MeshName } from "./MeshName";
import { MeshUtils, Rectangle3D } from "@dreamcam/babylon-tools";
import { TransparentUnlitMaterial } from "./materials/TransparentUnlitMaterial";
export class FadingHelper {
    get isBlocked() { return this._isBlocked; }
    constructor(scene, guiRootNode, logo) {
        this._fadingMeshes = [];
        this._blockPointerControls = [];
        this._fadeInStartedAt = undefined;
        this._fadeOutStartedAt = undefined;
        this._isBlocked = false;
        this._delayAsync = (ms) => {
            return new Promise(resolve => setTimeout(resolve, ms));
        };
        this._logo = logo;
        scene.onPrePointerObservable.add((eventData, _eventState) => {
            // Skip trigger clicks on fading:
            eventData.skipOnPointerObservable = this._isBlocked;
        });
        const fadingSkyboxPosition = Vector3.Zero().clone();
        const size = FadingHelper._fadingSkyboxSize;
        const createFadingPlane = (direction) => {
            const result = MeshUtils.createPlaneMesh(MeshName.fadingSkybox, size, size, scene, guiRootNode, DcRenderingGroup.fadingSkybox);
            result.mesh.position = fadingSkyboxPosition.clone().add(direction.scale(size / 2));
            result.mesh.lookAt(result.mesh.position.add(direction));
            return result;
        };
        this._fadingMeshes.push(createFadingPlane(Vector3.Forward()));
        this._fadingMeshes.push(createFadingPlane(Vector3.Right()));
        this._fadingMeshes.push(createFadingPlane(Vector3.Backward()));
        this._fadingMeshes.push(createFadingPlane(Vector3.Left()));
        this._fadingMeshes.push(createFadingPlane(Vector3.Up()));
        this._fadingMeshes.push(createFadingPlane(Vector3.Down()));
        this._fadingMat = new TransparentUnlitMaterial("fadingMat", scene);
        this._fadingMat.alphaMode = StandardMaterial.MATERIAL_ALPHABLEND;
        this._setAlpha(1);
        for (let mesh2d of this._fadingMeshes) {
            mesh2d.mesh.material = this._fadingMat;
            const block = new Rectangle3D("onboardingBlockRectangle");
            block.widthInPixels = mesh2d.m2px(size);
            block.heightInPixels = mesh2d.m2px(size);
            block.thickness = 0;
            block.background = "#00000000";
            mesh2d.texture.addControl(block);
            this._blockPointerControls.push(block);
        }
        this._unblockGUI();
        scene.registerBeforeRender(() => this._update());
        this._setFadeInTimeout(true);
    }
    _setFadeInTimeout(showLogo) {
        this._fadeInTimeout = setTimeout(() => {
            this.startFadeIn(showLogo);
        }, FadingHelper._fadeInTimeout);
    }
    _clearFadeInTimeout() {
        if (this._fadeInTimeout !== undefined) {
            clearTimeout(this._fadeInTimeout);
            this._fadeInTimeout = undefined;
        }
    }
    hideScene() {
        this._blockGUI();
        this._setAlpha(1);
    }
    startFadeIn(showLogo) {
        this._clearFadeInTimeout();
        this._fadeInStartedAt = Date.now();
        this._fadeOutStartedAt = undefined;
        this._setAlpha(1);
        if (showLogo) {
            this._logo.hide(FadingHelper._fadeInDuration);
        }
    }
    async fadeOutAsync(showLogo) {
        if (this._fadingMat.alpha > 0.9)
            return; // (already black)
        this._setFadeInTimeout(showLogo);
        if (showLogo) {
            this._logo.show(FadingHelper._fadeInDuration);
        }
        this._blockGUI();
        this._fadeInStartedAt = undefined;
        this._fadeOutStartedAt = Date.now();
        this._setAlpha(0);
        await this._delayAsync(FadingHelper._fadeOutDuration);
    }
    _update() {
        if (this._fadeInStartedAt !== undefined) {
            const duration = Date.now() - this._fadeInStartedAt;
            if (duration >= FadingHelper._fadeInDuration) {
                this._setAlpha(0);
                this._fadeInStartedAt = undefined;
                this._unblockGUI();
                // FadeIn finished.
            }
            else {
                const progressFactor = 1 - duration / FadingHelper._fadeInDuration;
                this._setAlpha(progressFactor * progressFactor);
            }
        }
        else if (this._fadeOutStartedAt !== undefined) {
            const duration = Date.now() - this._fadeOutStartedAt;
            if (duration >= FadingHelper._fadeOutDuration) {
                this._setAlpha(1);
                this._fadeOutStartedAt = undefined;
                // FadeOut finished.
            }
            else {
                const progressFactor = duration / FadingHelper._fadeOutDuration;
                this._setAlpha(progressFactor * progressFactor);
            }
        }
    }
    _setAlpha(alpha) {
        this._fadingMat.alpha = alpha;
    }
    // private _pickableMeshes: AbstractMesh[] = [];
    _blockGUI() {
        // console.log("%c BLOCK ", "background-color: gray; color: white");
        this._fadingMeshes.map(m => m.mesh.isPickable = true);
        setTimeout(() => {
            this._blockPointerControls.map(c => c.isPointerBlocker = true);
        }, 0);
        this._isBlocked = true;
    }
    _unblockGUI() {
        setTimeout(() => {
            // console.log("%c UNBLOCK ", "background-color: gray; color: white");
            this._blockPointerControls.map(c => c.isPointerBlocker = false);
            this._fadingMeshes.map(m => m.mesh.isPickable = false);
            this._isBlocked = false;
        }, 0);
    }
}
FadingHelper._fadeInTimeout = 10_000; // In case something goes wrong after fadeOut.
FadingHelper._fadeInDuration = 500;
FadingHelper._fadeOutDuration = 500;
FadingHelper._fadingSkyboxSize = 0.5;
